import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { logDev } from "@/lib/logger";
import { X, Copy, Mail, MessageSquare, Share2, Download, Send } from "lucide-react";

export default function BookingQRDialog({ bookingId, onClose }) {
  const [pngUrl, setPngUrl] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [{ data: u }, png] = await Promise.all([
          api.get(`/bookings/${bookingId}/selfcheckin-url`),
          api.get(`/bookings/${bookingId}/qr`, { responseType: "blob" }),
        ]);
        if (!active) return;
        const url = URL.createObjectURL(png.data);
        objectUrlRef.current = url;
        setPngUrl(url);
        setShareUrl(u.url);
      } catch (err) {
        if (!active) return;
        toast.error(err?.response?.data?.detail || "Errore caricamento QR");
        onClose?.();
      } finally { if (active) setLoading(false); }
    })();
    return () => {
      active = false;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [bookingId, onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiato");
    } catch { toast.error("Impossibile copiare"); }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Self check-in Xenia", url: shareUrl }); }
      catch (err) { logDev("shareNative: user cancelled", err); }
    } else { copy(); }
  };

  const sendDirect = async (channel) => {
    setSending(true);
    try {
      const { data } = await api.post(
        `/bookings/${bookingId}/send-checkin-link?channel=${channel}`
      );
      const ch = data.channels?.[0];
      const isMock = ch?.status === "mocked";
      toast.success(
        isMock
          ? `Inviato${ch?.type === "email" ? " (email mock — configura Resend per attivare)" : " (sms mock — configura Twilio per attivare)"}`
          : `Link inviato a ${ch?.to || ""}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  const subject = encodeURIComponent("Check-in online — Xenia");
  const body = encodeURIComponent(`Apri questo link prima di arrivare per fare il check-in dal telefono:\n\n${shareUrl}\n\nA presto!`);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="qr-dialog"
         onClick={onClose}>
      <div className="bg-white max-w-md w-full p-8 rounded-sm relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 hover:bg-stone-100" data-testid="qr-dialog-close">
          <X className="w-4 h-4" />
        </button>

        <p className="text-xs tracked text-blue-700 mb-2">SELF CHECK-IN</p>
        <h2 className="font-serif text-2xl mb-1">Manda il link al cliente</h2>
        <p className="text-stone-600 text-sm mb-6">Il cliente riceve un'email/SMS con un bottone "Apri check-in" — non deve scansionare nulla. In alternativa, mostragli il QR di persona.</p>

        {/* PRIMARY ACTION — direct send */}
        <div className="space-y-2 mb-6">
          <button onClick={() => sendDirect("email")} disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-stone-900 text-stone-50 px-4 py-3 text-sm rounded-sm hover:bg-stone-800 disabled:opacity-50"
            data-testid="qr-send-email">
            <Send className="w-4 h-4" /> {sending ? "Invio…" : "Invia link via email all'ospite"}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => sendDirect("sms")} disabled={sending}
              className="inline-flex items-center justify-center gap-2 border border-stone-300 px-3 py-2 text-sm hover:bg-stone-50 disabled:opacity-50"
              data-testid="qr-send-sms">
              <MessageSquare className="w-4 h-4" /> Anche via SMS
            </button>
            <button onClick={() => sendDirect("both")} disabled={sending}
              className="inline-flex items-center justify-center gap-2 border border-stone-300 px-3 py-2 text-sm hover:bg-stone-50 disabled:opacity-50"
              data-testid="qr-send-both">
              <Send className="w-4 h-4" /> Email + SMS
            </button>
          </div>
        </div>

        <details className="border-t border-stone-200 pt-4">
          <summary className="cursor-pointer text-xs tracked text-stone-500 mb-3 select-none">
            ALTRE OPZIONI · QR per scansionare di persona / copia manuale
          </summary>

        <div className="bg-white border border-stone-200 p-6 flex items-center justify-center min-h-[260px] mt-3">
          {loading || !pngUrl ? (
            <span className="text-stone-400 text-sm">Generazione…</span>
          ) : (
            <img src={pngUrl} alt="QR Code" className="w-56 h-56" data-testid="qr-image" />
          )}
        </div>

        <div className="mt-4 border border-stone-200 bg-stone-50 p-3 text-xs break-all font-mono text-stone-700" data-testid="qr-share-url">
          {shareUrl || "…"}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
          <button onClick={copy} data-testid="qr-copy"
            className="inline-flex items-center justify-center gap-2 border border-stone-300 px-3 py-2 hover:bg-stone-50">
            <Copy className="w-4 h-4" /> Copia link
          </button>
          <button onClick={shareNative} data-testid="qr-share"
            className="inline-flex items-center justify-center gap-2 border border-stone-300 px-3 py-2 hover:bg-stone-50">
            <Share2 className="w-4 h-4" /> Condividi
          </button>
          <a href={`mailto:?subject=${subject}&body=${body}`} data-testid="qr-email"
            className="inline-flex items-center justify-center gap-2 border border-stone-300 px-3 py-2 hover:bg-stone-50">
            <Mail className="w-4 h-4" /> Email (mia app)
          </a>
          <a href={`sms:?body=${body}`} data-testid="qr-sms"
            className="inline-flex items-center justify-center gap-2 border border-stone-300 px-3 py-2 hover:bg-stone-50">
            <MessageSquare className="w-4 h-4" /> SMS (mio cell)
          </a>
          {pngUrl && (
            <a href={pngUrl} download={`xenia-checkin-${bookingId.slice(0,8)}.png`} data-testid="qr-download"
              className="col-span-2 inline-flex items-center justify-center gap-2 bg-stone-100 text-stone-900 px-3 py-2 hover:bg-stone-200">
              <Download className="w-4 h-4" /> Scarica QR (PNG)
            </a>
          )}
        </div>
        </details>
      </div>
    </div>
  );
}
